/**
 * Canvas renderer — sport-agnostic.
 *
 * It consumes `core` state plus a `SportPack` and knows nothing about any
 * particular sport: every marking, colour, radius and label comes from the pack.
 * Rendering is a pure function of the scene, which is what makes the same code
 * drive the live board, an exported PNG and the recorded video stream.
 */

import type { BoardView, Id, Marking, PlayDocument, PoseMap, Rect, Shape, ShapeKind, SportPack, Surface, Vec2 } from '../core/index.ts';
import { findEntityType, findRole, findTeam, normalizeRect, viewWindow } from '../core/index.ts';
import {
  canvasTransform,
  computeViewport,
  cropMargins,
  NO_MARGINS,
  px,
  toCanvas,
  type FieldMargins,
  type Viewport,
  type ViewRotation,
} from './viewport.ts';

export interface DraftShape {
  readonly kind: ShapeKind;
  readonly points: readonly Vec2[];
  readonly color: string;
  readonly width: number;
}

export interface Scene {
  readonly pack: SportPack;
  readonly doc: PlayDocument;
  readonly poses: PoseMap;
  readonly shapes: readonly Shape[];
  readonly selectedIds: readonly string[];
  readonly hoverId: string | null;
  readonly draft: DraftShape | null;
  /** Faint trace of each entity's route through the keyframes. */
  readonly showRoleLabels: boolean;
  /** Draw the venue around the field, and reserve the room it needs. */
  readonly showVenueDetail: boolean;
  /** The in-progress rubber-band rectangle, drawn when a box select is under way. */
  readonly marquee: Rect | null;
  /** Which stretch of the length is on screen; the full field when absent. */
  readonly view?: BoardView;
}

const FONT_STACK =
  '"Inter", "Segoe UI", system-ui, -apple-system, "Helvetica Neue", Arial, sans-serif';

/**
 * The surround, converted from the surface's own units into field units.
 *
 * Each axis is divided by its own dimension: on a 25 x 20 m field a metre is
 * 1/25 in x and 1/20 in y, so a single scale factor would skew the surround.
 * `viewport.ts` never sees the surface, which is what keeps it pure arithmetic.
 */
export function surroundMargins(surface: Surface): FieldMargins {
  const surround = surface.surround;
  if (!surround) return NO_MARGINS;
  return {
    left: surround.left / surface.dimensions.length,
    right: surround.right / surface.dimensions.length,
    top: surround.top / surface.dimensions.width,
    bottom: surround.bottom / surface.dimensions.width,
  };
}

export function render(ctx: CanvasRenderingContext2D, scene: Scene, width: number, height: number): Viewport {
  const { surface } = scene.pack;
  // Reserved only when the detail is actually drawn, so the default board keeps
  // the full canvas — the surround would otherwise shrink it to make room for
  // content nobody asked to see.
  const view = scene.view ?? 'full';
  const window = viewWindow(view);
  const margins = cropMargins(scene.showVenueDetail ? surroundMargins(surface) : NO_MARGINS, window);
  const vp = computeViewport(width, height, surface.aspectRatio, margins, VIEW_ROTATION[view]);

  ctx.save();
  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = BACKDROP;
  ctx.fillRect(0, 0, width, height);
  // A half view is turned so its goal is at the top. Everything below draws
  // into the unturned drawing space; only text is turned back upright.
  ctx.transform(...canvasTransform(vp));
  // A cropped side ends at the window's edge: nothing beyond it is drawn, so
  // the other half does not bleed into the padding. An uncropped side keeps
  // the whole canvas, so goals and labels outside the field box still show.
  const clipLeft = window.from > 0 ? toCanvas({ x: window.from, y: 0 }, vp).x : 0;
  const clipRight = window.to < 1 ? toCanvas({ x: window.to, y: 0 }, vp).x : vp.canvasW;
  ctx.beginPath();
  ctx.rect(clipLeft, 0, clipRight - clipLeft, vp.canvasH);
  ctx.clip();
  drawBackdrop(ctx, scene.pack, vp);
  drawMarkings(ctx, surface.markings, vp);
  if (scene.showVenueDetail && surface.venueMarkings) drawMarkings(ctx, surface.venueMarkings, vp);
  // Derived from the visible set, so whichever pass is newest at this point in
  // the play reads as solid rather than every pass fading off an unseen one.
  const passAlpha = passOpacities(scene.shapes);
  for (const shape of scene.shapes) drawShape(ctx, shape, vp, false, passAlpha.get(shape.id) ?? 1);
  // The draft belongs to no phase: it is not in the document until released.
  if (scene.draft) drawShape(ctx, { ...scene.draft, id: 'draft', keyframeId: '' }, vp, true);
  drawEntities(ctx, scene, vp);
  if (scene.marquee) drawMarquee(ctx, scene.marquee, vp);
  ctx.restore();

  return vp;
}

/* ------------------------------------------------------------------ */
/* Surface                                                             */
/* ------------------------------------------------------------------ */

const BACKDROP = '#071a2b';

/**
 * Where a position label sits: below the cap, `POSITION_OFFSET` cap radii
 * down, at `POSITION_FONT` radii tall. Shared with hit testing, so a
 * double-click lands on the label the coach sees.
 */
export const POSITION_OFFSET = 1.25;
export const POSITION_FONT = 0.72;

/** The position text shown under a player: the coach's own name, else the role's short form. */
export function positionLabel(entity: { readonly roleLabel?: string }, role: { readonly short: string } | undefined): string | undefined {
  return entity.roleLabel ?? role?.short;
}

/** Each half is turned so its goal sits at the top of the board. */
const VIEW_ROTATION: Readonly<Record<BoardView, ViewRotation>> = { full: 0, left: 90, right: -90 };

/**
 * Draws text centred on `at` in drawing space but upright on screen, whatever
 * the view's turn. `dx`/`dy` offset it in the upright frame, so a label meant
 * to sit under a cap still sits under it in a turned view.
 */
function fillUpright(
  ctx: CanvasRenderingContext2D,
  vp: Viewport,
  text: string,
  at: Vec2,
  dx = 0,
  dy = 0,
): void {
  if (vp.rotation === 0) {
    ctx.fillText(text, at.x + dx, at.y + dy);
    return;
  }
  ctx.save();
  ctx.translate(at.x, at.y);
  ctx.rotate((-vp.rotation * Math.PI) / 180);
  ctx.fillText(text, dx, dy);
  ctx.restore();
}

function drawBackdrop(ctx: CanvasRenderingContext2D, pack: SportPack, vp: Viewport): void {
  ctx.fillStyle = BACKDROP;
  ctx.fillRect(0, 0, vp.canvasW, vp.canvasH);

  const gradient = ctx.createLinearGradient(vp.originX, vp.originY, vp.originX, vp.originY + vp.fieldH);
  gradient.addColorStop(0, shade(pack.surface.background, 1.18));
  gradient.addColorStop(0.5, pack.surface.background);
  gradient.addColorStop(1, shade(pack.surface.background, 0.82));
  ctx.fillStyle = gradient;
  ctx.fillRect(vp.originX, vp.originY, vp.fieldW, vp.fieldH);
}

function drawMarkings(ctx: CanvasRenderingContext2D, markings: readonly Marking[], vp: Viewport): void {
  for (const marking of markings) {
    ctx.save();
    ctx.globalAlpha = marking.opacity ?? 1;
    ctx.lineCap = 'butt';
    ctx.lineJoin = 'miter';

    switch (marking.kind) {
      case 'line': {
        const a = toCanvas(marking.from, vp);
        const b = toCanvas(marking.to, vp);
        ctx.strokeStyle = marking.color;
        ctx.lineWidth = px(marking.width ?? 0.004, vp);
        ctx.setLineDash((marking.dash ?? []).map((d) => px(d, vp)));
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
        break;
      }
      case 'rect': {
        const a = toCanvas(marking.at, vp);
        const w = marking.size.w * vp.fieldW;
        const h = marking.size.h * vp.fieldH;
        if (marking.fill) {
          ctx.fillStyle = marking.fill;
          ctx.fillRect(a.x, a.y, w, h);
        }
        ctx.strokeStyle = marking.color;
        ctx.lineWidth = px(marking.width ?? 0.004, vp);
        ctx.setLineDash((marking.dash ?? []).map((d) => px(d, vp)));
        ctx.strokeRect(a.x, a.y, w, h);
        break;
      }
      case 'circle': {
        const a = toCanvas(marking.at, vp);
        ctx.beginPath();
        ctx.arc(a.x, a.y, px(marking.radius, vp), 0, Math.PI * 2);
        if (marking.fill) {
          ctx.fillStyle = marking.fill;
          ctx.fill();
        }
        ctx.strokeStyle = marking.color;
        ctx.lineWidth = px(marking.width ?? 0.004, vp);
        ctx.stroke();
        break;
      }
      case 'polyline': {
        ctx.strokeStyle = marking.color;
        ctx.lineWidth = px(marking.width ?? 0.004, vp);
        ctx.setLineDash((marking.dash ?? []).map((d) => px(d, vp)));
        ctx.beginPath();
        marking.points.forEach((point, index) => {
          const s = toCanvas(point, vp);
          if (index === 0) ctx.moveTo(s.x, s.y);
          else ctx.lineTo(s.x, s.y);
        });
        ctx.stroke();
        break;
      }
      case 'label': {
        const a = toCanvas(marking.at, vp);
        ctx.fillStyle = marking.color;
        ctx.font = `600 ${px(marking.size, vp)}px ${FONT_STACK}`;
        ctx.textAlign = marking.align ?? 'center';
        ctx.textBaseline = 'middle';
        fillUpright(ctx, vp, marking.text, a);
        break;
      }
    }
    ctx.restore();
  }
}

/* ------------------------------------------------------------------ */
/* Pass recency                                                        */
/* ------------------------------------------------------------------ */

/** Each older pass is this fraction of the one drawn after it. */
const PASS_DECAY = 0.8;
/**
 * The faintest a pass may become. Passes are dashed and carry a drop shadow, so
 * they lose definition quickly — below this they stop reading as a line.
 */
const PASS_FLOOR = 0.35;

/**
 * Opacity per pass id, newest at full strength and each older one fainter.
 *
 * A fixed step rather than a spread across the range: a spread would pin the
 * oldest pass to the floor and re-space everything in between, so drawing a new
 * pass would silently change the opacity of every pass already on the board.
 * With a fixed step the existing arrows never move.
 *
 * `shapes` is the visible set in creation order, and only passes take a rank —
 * a move or a shot drawn mid-sequence is not part of the passing chain and must
 * not age the passes around it. Kinds other than `pass` get no entry at all, so
 * a caller that finds nothing draws at full opacity.
 */
export function passOpacities(shapes: readonly Shape[]): ReadonlyMap<Id, number> {
  const passes = shapes.filter((shape) => shape.kind === 'pass');
  const alpha = new Map<Id, number>();
  passes.forEach((pass, index) => {
    const stepsBack = passes.length - 1 - index;
    alpha.set(pass.id, Math.max(PASS_FLOOR, PASS_DECAY ** stepsBack));
  });
  return alpha;
}

/* ------------------------------------------------------------------ */
/* Shapes                                                              */
/* ------------------------------------------------------------------ */

function drawShape(
  ctx: CanvasRenderingContext2D,
  shape: Shape,
  vp: Viewport,
  isDraft: boolean,
  opacity = 1,
): void {
  if (shape.points.length < 2) return;
  const points = shape.points.map((p) => toCanvas(p, vp));
  const lineWidth = px(shape.width, vp);

  ctx.save();
  ctx.strokeStyle = shape.color;
  ctx.fillStyle = shape.color;
  ctx.lineWidth = lineWidth;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  // Scales the drop shadow and the arrowhead too, so the arrow fades as one.
  ctx.globalAlpha = isDraft ? 0.85 : opacity;
  ctx.shadowColor = 'rgba(0,0,0,0.45)';
  ctx.shadowBlur = lineWidth * 1.2;

  switch (shape.kind) {
    case 'pass':
      ctx.setLineDash([lineWidth * 2.4, lineWidth * 2.0]);
      break;
    case 'shot':
      ctx.lineWidth = lineWidth * 1.6;
      break;
    default:
      ctx.setLineDash([]);
  }

  strokeSmooth(ctx, points);

  ctx.setLineDash([]);
  if (shape.kind !== 'freehand') {
    const tip = points[points.length - 1]!;
    const before = points[Math.max(0, points.length - 2)]!;
    drawArrowHead(ctx, before, tip, lineWidth * (shape.kind === 'shot' ? 4.2 : 3.4));
  }
  if (shape.kind === 'move' || shape.kind === 'shot') {
    // A short cross-bar at the origin reads as "this player starts here".
    drawOriginTick(ctx, points[0]!, points[1]!, lineWidth * 2.2);
  }
  ctx.restore();
}

/** Quadratic smoothing through segment midpoints — kills touch jitter. */
function strokeSmooth(ctx: CanvasRenderingContext2D, points: readonly Vec2[]): void {
  ctx.beginPath();
  const first = points[0]!;
  ctx.moveTo(first.x, first.y);
  if (points.length === 2) {
    ctx.lineTo(points[1]!.x, points[1]!.y);
  } else {
    for (let i = 1; i < points.length - 1; i += 1) {
      const current = points[i]!;
      const next = points[i + 1]!;
      ctx.quadraticCurveTo(current.x, current.y, (current.x + next.x) / 2, (current.y + next.y) / 2);
    }
    const last = points[points.length - 1]!;
    ctx.lineTo(last.x, last.y);
  }
  ctx.stroke();
}

function drawArrowHead(ctx: CanvasRenderingContext2D, from: Vec2, tip: Vec2, size: number): void {
  const angle = Math.atan2(tip.y - from.y, tip.x - from.x);
  const spread = 0.42;
  ctx.beginPath();
  ctx.moveTo(tip.x, tip.y);
  ctx.lineTo(tip.x - size * Math.cos(angle - spread), tip.y - size * Math.sin(angle - spread));
  ctx.lineTo(tip.x - size * Math.cos(angle + spread), tip.y - size * Math.sin(angle + spread));
  ctx.closePath();
  ctx.fill();
}

function drawOriginTick(ctx: CanvasRenderingContext2D, origin: Vec2, next: Vec2, size: number): void {
  const angle = Math.atan2(next.y - origin.y, next.x - origin.x) + Math.PI / 2;
  ctx.beginPath();
  ctx.moveTo(origin.x - Math.cos(angle) * size, origin.y - Math.sin(angle) * size);
  ctx.lineTo(origin.x + Math.cos(angle) * size, origin.y + Math.sin(angle) * size);
  ctx.stroke();
}

/* ------------------------------------------------------------------ */
/* Entities                                                            */
/* ------------------------------------------------------------------ */

function drawEntities(ctx: CanvasRenderingContext2D, scene: Scene, vp: Viewport): void {
  const { pack, doc, poses } = scene;

  // Balls and props on top of players.
  const ordered = [...doc.entities].sort((a, b) => Number(a.typeId === 'ball') - Number(b.typeId === 'ball'));

  for (const entity of ordered) {
    const position = poses[entity.id];
    if (!position) continue;
    const type = findEntityType(pack, entity.typeId);
    if (!type) continue;

    const s = toCanvas(position, vp);
    const radius = px(type.radius, vp);
    const isSelected = scene.selectedIds.includes(entity.id);
    const isHovered = scene.hoverId === entity.id;

    if (type.shape === 'ball') {
      drawBall(ctx, s, radius, type.color ?? '#ffd83d', isSelected || isHovered);
      continue;
    }

    const team = findTeam(pack, entity.teamId);
    const role = findRole(pack, entity.roleId);
    const fill = role?.color ?? team?.color ?? '#dddddd';
    const textColor = role?.textColor ?? team?.textColor ?? '#111111';
    const outline = team?.outline ?? '#000000';

    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.5)';
    ctx.shadowBlur = radius * 0.6;
    ctx.shadowOffsetY = radius * 0.16;
    ctx.beginPath();
    ctx.arc(s.x, s.y, radius, 0, Math.PI * 2);
    ctx.fillStyle = fill;
    ctx.fill();
    ctx.restore();

    ctx.beginPath();
    ctx.arc(s.x, s.y, radius, 0, Math.PI * 2);
    ctx.strokeStyle = isSelected ? '#ffffff' : outline;
    ctx.lineWidth = Math.max(1, radius * (isSelected ? 0.22 : 0.12));
    ctx.stroke();

    if (isSelected || isHovered) {
      ctx.beginPath();
      ctx.arc(s.x, s.y, radius * 1.45, 0, Math.PI * 2);
      ctx.strokeStyle = isSelected ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.4)';
      ctx.lineWidth = Math.max(1, radius * 0.1);
      ctx.setLineDash([radius * 0.4, radius * 0.35]);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    if (entity.label) {
      ctx.fillStyle = textColor;
      ctx.font = `700 ${radius * 1.05}px ${FONT_STACK}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      fillUpright(ctx, vp, entity.label, s, 0, radius * 0.04);
    }

    // The coach's own name for the position wins over the role's short form.
    const positionName = positionLabel(entity, role);
    if (scene.showRoleLabels && positionName) {
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.font = `600 ${radius * POSITION_FONT}px ${FONT_STACK}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      fillUpright(ctx, vp, positionName, s, 0, radius * POSITION_OFFSET);
    }
  }
}

function drawBall(ctx: CanvasRenderingContext2D, s: Vec2, radius: number, color: string, highlighted: boolean): void {
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.55)';
  ctx.shadowBlur = radius * 0.9;
  ctx.beginPath();
  ctx.arc(s.x, s.y, radius, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.restore();

  ctx.beginPath();
  ctx.arc(s.x, s.y, radius, 0, Math.PI * 2);
  ctx.strokeStyle = highlighted ? '#ffffff' : 'rgba(0,0,0,0.55)';
  ctx.lineWidth = Math.max(1, radius * 0.22);
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(s.x, s.y, radius * 0.55, -Math.PI * 0.15, Math.PI * 0.85);
  ctx.strokeStyle = 'rgba(0,0,0,0.4)';
  ctx.lineWidth = Math.max(1, radius * 0.16);
  ctx.stroke();
}

/** The rubber-band selection rectangle, drawn over entities so it reads as the active tool. */
function drawMarquee(ctx: CanvasRenderingContext2D, marquee: Rect, vp: Viewport): void {
  const { min, max } = normalizeRect(marquee);
  const a = toCanvas(min, vp);
  const b = toCanvas(max, vp);
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  const w = Math.abs(b.x - a.x);
  const h = Math.abs(b.y - a.y);

  ctx.save();
  ctx.setLineDash([4, 3]);
  ctx.strokeStyle = '#38bdf8';
  ctx.lineWidth = 1;
  ctx.fillStyle = 'rgba(56,189,248,0.10)';
  ctx.fillRect(x, y, w, h);
  ctx.strokeRect(x, y, w, h);
  ctx.setLineDash([]);
  ctx.restore();
}

/* ------------------------------------------------------------------ */

/** Multiplies a #rrggbb colour's channels, clamped. Used for the water gradient. */
function shade(hex: string, factor: number): string {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return hex;
  const value = parseInt(match[1]!, 16);
  const channels = [(value >> 16) & 255, (value >> 8) & 255, value & 255].map((c) =>
    Math.round(Math.min(255, Math.max(0, c * factor))),
  );
  return `rgb(${channels[0]}, ${channels[1]}, ${channels[2]})`;
}
